import { useEffect, useRef, useState } from "react";
import { Button, Flex, Heading, Stack } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import Reaptcha from "reaptcha";
import { Input } from "@/components";
import { useResetPassword } from "@/hooks";
import { getApplicationName ,showToast } from "@/utils";
import { BannerLateral } from "@/layouts";
import { getTenantConfig } from "@/config/tenantConfig";

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Digite um e-mail válido!")
    .required("O campo e-mail é obrigatório!"),
});

type FormValues = {
  email: string;
};

type CaptchaFailureReason = "execute" | "error" | "expire" | "timeout";

const CAPTCHA_TIMEOUT_MS = 60_000;

const EXPECTED_CAPTCHA_REASONS: CaptchaFailureReason[] = ["expire", "timeout"];

const ResetPassword: React.FC = () => {
  const { sendEmailResetPassword } = useResetPassword();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({ resolver: yupResolver(validationSchema) });
  const applicationName = getApplicationName({});
  const [loading, setLoading] = useState(false);
  const captchaRef = useRef<Reaptcha>(null);
  const captchaAttemptActiveRef = useRef(false);
  const captchaTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearCaptchaTimeout = () => {
    if (captchaTimeoutRef.current) clearTimeout(captchaTimeoutRef.current);
    captchaTimeoutRef.current = undefined;
  };

  useEffect(() => {
    return clearCaptchaTimeout;
  }, []);

  const onCaptchaError = (reason: CaptchaFailureReason, error?: any) => {
    const log = EXPECTED_CAPTCHA_REASONS.includes(reason)
      ? console.warn
      : console.error;

    log("[redefinir-senha] falha no captcha", {
      reason,
      message: error?.message,
    });

    if (!captchaAttemptActiveRef.current) return;

    captchaAttemptActiveRef.current = false;
    clearCaptchaTimeout();
    setLoading(false);
    showToast({
      type: "error",
      message: "Não foi possível validar o captcha. Tente novamente.",
    });
  };

  const onSubmit = handleSubmit(async () => {
    setLoading(true);
    captchaAttemptActiveRef.current = true;
    try {
      clearCaptchaTimeout();
      captchaTimeoutRef.current = setTimeout(
        () => onCaptchaError("timeout"),
        CAPTCHA_TIMEOUT_MS
      );
      await captchaRef.current?.reset();
      await captchaRef.current?.execute();
    } catch (error) {
      onCaptchaError("execute", error);
    }
  });

  const onCaptchaVerify = async (captchaToken: string) => {
    if (!captchaAttemptActiveRef.current) return;

    captchaAttemptActiveRef.current = false;
    clearCaptchaTimeout();
    try {
      await sendEmailResetPassword(getValues("email"), captchaToken);
      showToast({
        message: "Link enviado para seu e-mail, por favor, verifique!",
        type: "success",
      });

      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (error) {
      const errors = error?.response?.data?.errors;

      console.error("[redefinir-senha] falha ao enviar o link", {
        status: error?.response?.status,
        codes: errors?.map((item) => item?.code),
        message: error?.message,
      });

      if (errors?.length) {
        errors.forEach((item) => {
          showToast({
            type: "error",
            message: `${item?.code} - ${item?.description}`,
          });
        });
      } else {
        showToast({
          type: "error",
          message: "Não foi possível enviar o link de redefinição. Tente novamente.",
        });
      }

      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex flexDir={["column", "column", "column", "row"]} overflow="hidden">
      <Flex align="center" w={["100%", "100%", "100%", "50%"]} justify="center">
        <Flex
          as="form"
          direction="column"
          align="flex-start"
          justify="center"
          h="100vh"
          w="380px"
          onSubmit={onSubmit}
          gap="6"
        >
           <img src={getTenantConfig().logoUrl} alt="Company Logo" />
          <Flex w="100%" flexDirection="column" mb="40px">
            <Heading as="h3" mb="10px">
              Trocar senha
            </Heading>
            <Flex color="gray.400">
              Informe o seu email e enviaremos um link para você realizar a
              troca de senha
            </Flex>
          </Flex>
          <Stack w="100%">
            <Input
              label="Email"
              type="text"
              name="email"
              placeholder={"Insira seu email"}
              className="mb-3"
              register={{ ...register("email") }}
              errors={errors?.email}
              aria-label={"Insira seu email"}
              aria-invalid={errors.email ? "true" : "false"}
              fontSize="1rem"
              labelColor="black"
              mb="30px"
            />

            <Reaptcha
              sitekey={`${import.meta.env.VITE_PUBLIC_SITE_KEY_GOOGLE_RECAPTCHA}`}
              ref={captchaRef}
              onVerify={onCaptchaVerify}
              onError={() => onCaptchaError("error")}
              onExpire={() => onCaptchaError("expire")}
              size="invisible"
              badge="bottomleft"
            />

            <Button
              isLoading={loading}
              type="submit"
              variant="outline"
              fontSize="1rem"
              py="1rem"
              w="150px"
              mt="40px!important"
            >
              Resetar Senha
            </Button>
          </Stack>
        </Flex>
      </Flex>

      <BannerLateral />
    </Flex>
  );
};

export default ResetPassword;
